import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  Collapse,
  Input,
  Label,
  Tooltip,
  UncontrolledCollapse,
  Alert
} from 'reactstrap';

import { appState } from '../../AppState';
import ConnectriaTicketsService from '../../services/ConnectriaTicketsService';
import PortalService from '../../services/PortalService';
import useAsyncEffect from '../../utility/use-async-effect';
import useTriaTicketsState, { createdBy } from './ConnectriaTicketsState';

import FileService from '../../services/FileService';
import YetiContacts from '../../services/YetiContacts';
import YetiTicketProvider from '../../services/YetiTickets';
import Attachments from '../Attachments/Attachments';
import EmailSelector from '../EmailSelector/EmailSelector';
import AutoCompleteSelectForm from '../InputComponents/AutoCompleteSelectForm';
import LightEdgeHistoryItem from './LightEdgeHistoryItem';
import { getProviderConfig } from './ProviderConfig';
import { TicketDataProvider, useTicketData } from './TicketDataProvider';
import { PROVIDERS } from './constants';
import { downloadBase64, prependServiceIdToDescription } from './utils';

const MAX_ATTACHMENT_SIZE_MB = 4.4;

var moment = require('moment-timezone');

const ConnectriaResponseRating = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const hasRating = props.userRatings.filter((e) => e.ticket_history_id === props.commentId);
  const { commentsError } = useTicketData();
  const toggle = (rating) => {
    setIsOpen(!isOpen);
    //if closing the rating and there is a historical value check for it first
    setRating(rating == null ? (hasRating?.length > 0 ? hasRating[0]?.rating : null) : rating);
  };
  useEffect(() => {
    if (hasRating?.length > 0) {
      setRating(hasRating[0].rating);
      setComment(hasRating[0].comment);
    }
  }, [hasRating]);

  const doRating = async () => {
    let endpoint =
      hasRating.length > 0
        ? ConnectriaTicketsService.updateTicketCommentRating(hasRating[0].id, rating, comment)
        : ConnectriaTicketsService.createTicketCommentRating(props.commentId, rating, comment);
    setIsLoading(true);
    let result = await endpoint;
    setIsLoading(false);
    toggle(rating);
  };
  const deleteRating = async () => {
    setIsLoading(true);
    let result = await ConnectriaTicketsService.deleteTicketCommentRating(hasRating[0].id);
    toggle(0);
    setIsLoading(false);
  };

  if (commentsError) {
    return (
        <>
          <Alert color="danger">
            {commentsError?.message}
          </Alert>
          <Button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-sm mt-1 d-block mx-auto"
          >
            Retry
          </Button>
        </>
    );

  }

  return (
    <div
      className={'p2 row '}
      style={{
        backgroundColor: '#e9e9e9',
        paddingLeft: 12 + 'px',
        borderTop: 0.0625 + 'rem solid #d5d9dd',
      }}
    >
      <p className="w-100">
        Was the following response helpful? Click here to rate:
        <div className="parent">
          <span
            id="rating-1"
            onClick={() => toggle(1)}
            className={(rating >= 1 ? 'fas rated' : 'far') + ' fa-star comment-rating'}
          />
          <span
            id="rating-2"
            onClick={() => toggle(2)}
            className={(rating >= 2 ? 'fas rated' : 'far') + ' fa-star comment-rating'}
          />
          <span
            id="rating-3"
            onClick={() => toggle(3)}
            className={(rating >= 3 ? 'fas rated' : 'far') + ' fa-star comment-rating'}
          />
          <span
            id="rating-4"
            onClick={() => toggle(4)}
            className={(rating >= 4 ? 'fas rated' : 'far') + ' fa-star comment-rating'}
          />
          <span
            id="rating-5"
            onClick={() => toggle(5)}
            className={(rating >= 5 ? 'fas rated' : 'far') + ' fa-star comment-rating'}
          />
        </div>
        <Collapse isOpen={isOpen} className="pr-1">
          <Card>
            <CardBody>
              <Input
                type="textarea"
                rows="4"
                onChange={(e) => setComment(e.target.value)}
                className="w-100 form-control"
                value={comment}
                placeholder="Add a comment about the quality of support you received."
              />
            </CardBody>
            <CardFooter>
              <div className="text-right">
                {hasRating.length > 0 ? (
                  <span
                    style={{ lineHeight: 35.6 + 'px' }}
                    className="pull-left cursor"
                    onClick={() => {
                      if (window.confirm('Are you sure you wish to delete this rating?')) deleteRating();
                    }}
                  >
                    [ remove my rating ]
                  </span>
                ) : (
                  <></>
                )}
                <Button type="button" onClick={() => toggle(null)} className="btn-sm btn-danger">
                  Close
                </Button>
                <Button disabled={isLoading} type="button" onClick={() => doRating()} className="btn-sm">
                  Submit
                </Button>
              </div>
            </CardFooter>
          </Card>
        </Collapse>
      </p>
    </div>
  );
};

function HistoryItem(props) {
  const [stateApp, stateAppActions] = appState();

  const momentTzGuess = moment.tz.guess();
  const commentCreatedTs = props.comment.created;
  const created = moment(commentCreatedTs, 'YYYY-MM-DDThh:mm:ss.SSSZ').tz(momentTzGuess).format('LLL z');
  const customerId = props.ticket?.customer_id || 0;

  //if the ticket wasn't created by Connectria assume all commenters from Connectria are "left-side"
  if (customerId) {
    return (
      <div key={props.index} className={`mt-2 ${props.comment.user.customer_id > 0 ? 'text-left' : 'text-left'}`}>
        <div className="p-2 row" style={{ backgroundColor: '#e9e9e9', paddingBottom: 0 + 'px !important' }}>
          <div style={{ width: 90 + '%' }} className="col">
            <p className="  mb-0">
              Comment by:{' '}
              <span style={{ textTransform: 'capitalize', fontWeight: 700 }}>{createdBy(props.comment.user)}</span> of{' '}
              <span style={{ fontWeight: 700 }}>
                {props.comment.user.customer_id > 0 ? stateApp.userInfo.organization_name : 'Lightedge'}
              </span>
            </p>

            <p className=" mb-0">{created}</p>
          </div>
          <div style={{ maxWidth: 10 + '%' }} className="col center-flex-item">
            {props.comment.user.customer_id == 0 ? (
              <img
                style={{ width: 30 + 'px' }}
                src="data:image/png;base64,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"
              />
            ) : (
              null
            )}
          </div>
        </div>
        {props.comment.user.customer_id == 0 ? (
          <ConnectriaResponseRating
            userRatings={props?.userRatings}
            commentId={props?.comment?.id}
          />
        ) : (
          null
        )}
        <p className="pb-3 pt-1" style={{ fontSize: 0.97 + 'rem', whiteSpace: 'pre-wrap' }}>
          {props.comment.comment}
        </p>
      </div>
    );
  } else {
    //created by connectria. if comment comes from a different team assume it's "left-side"
    return (
      <div
        key={props.index}
        className={`mt-2 ${props?.comment?.user?.primary_team_id !== props?.ticket?.team_id ? 'text-left' : 'text-left'}`}
      >
        <p>{props.comment.comment}</p>
        <blockquote className={`primary ${props?.comment?.user?.primary_team_id !== props?.ticket?.team_id ? 'right' : ''}`}>
          {createdBy(props?.comment?.user)} - {moment(props?.comment?.created, 'YYYY-MM-DDThh:mm:ss.SSSZ').calendar()}
        </blockquote>
      </div>
    );
  }
}

const TicketComments = ({ ticket }) => {
  const { comments, ratings, isLoadingComments, provider, commentsError } = useTicketData();

  if (isLoadingComments) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  }

  if (commentsError) {
    return (
        <>
          <Alert color="danger">
            {commentsError?.message}
          </Alert>
          <Button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-sm mt-1 d-block mx-auto"
          >
            Retry
          </Button>
        </>
    );
  }

  const HistoryComponent = provider === PROVIDERS.CONNECTRIA ? HistoryItem : LightEdgeHistoryItem;

  return (
    <>
      {comments == null || comments.length === 0 ? (
        <div />
      ) : (
        comments.map((comment, index) => (
          <HistoryComponent
            userRatings={ratings}
            index={index}
            comment={comment}
            ticket={ticket}
            key={index}
          />
        ))
      )}
    </>
  );
};

const FileTypeIcon = ({ filename }) => {
  const extension = filename.split('.').pop().toLowerCase();
  switch (extension) {
    case 'pdf':
      return <i className="fa fa-file-pdf" />;
    case 'txt':
      return <i className="fa fa-file-alt" />;
    case 'gif':
    case 'jpg':
    case 'jpeg':
    case 'tif':
    case 'png':
      return <i className="fa fa-file-image" />;
    case 'doc':
    case 'docx':
    case 'docm':
      return <i className="fa fa-file-word" />;
    case 'xls':
    case 'xlsx':
    case 'xlsm':
      return <i className="fa fa-file-excel" />;
    default:
      return <i className="fa fa-file" />;
  }
};

const Attachment = (props) => {
  const { provider } = useTicketData();
  const [downloading, setDownloading] = useState(false);

  const download = async (e, file) => {
    e.preventDefault();
    setDownloading(true);

    try {
      if (provider === PROVIDERS.CONNECTRIA) {
        const attachmentUrl = await ConnectriaTicketsService.getAttachmentUrl(props.ticketId, file.id);
        window.open(attachmentUrl, '_blank');
      } else {
        const response = await YetiTicketProvider.getAttachment(file.id, props.ticketId);
        const filename = file.filename || 'downloaded_file';
        const mimeType = file.mimetype || 'application/octet-stream';
        //downloadBlob(response.data, filename, mimeType);
        downloadBase64(response.data, filename, mimeType);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <a
      onClick={(e) => {
        download(e, props.file);
      }}
    >
      <div className="attachment-block rounded">
        <FileTypeIcon filename={props.file.filename} className="attachment-icon" />
        <div>
          <h4>{props.file.filename}</h4>
          <small>{moment(props.file.created, 'YYYY-MM-DDThh:mm:ss.SSSZ').calendar()}</small>
        </div>
        <div style={{ margin: 'auto' }}>{downloading ? <i className="fas fa-2x fa-spinner fa-spin" /> : <></>}</div>
      </div>
    </a>
  );
};

const BladeTabAttachments = ({ ticketId }) => {
  const { attachments, isLoadingAttachments, attachmentsError } = useTicketData();

  if (isLoadingAttachments) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  }

  if (attachmentsError) {
    return (
        <>
          <Alert color="danger">
            {attachmentsError?.message}
          </Alert>
          <Button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-sm mt-3 d-block mx-auto"
          >
            Retry
          </Button>
        </>
    );
  }

  return (
    <>
      {!attachments || attachments.length === 0 ? (
        <div className="text-center mt-4 text-bold">
          <i className="fas fa-exclamation-triangle" /> No Attachments
        </div>
      ) : (
        <>
          {attachments.map((file) => (
            <Attachment ticketId={ticketId} file={file} key={ticketId} />
          ))}
        </>
      )}
    </>
  );
};

const TicketCloseForm = ({ id, reopen }) => {
  const [, stateAppActions] = appState();
  const [, stateTicketActions] = useTriaTicketsState();
  const [closeComment, setCloseComment] = useState('');
  const [closeRating, setCloseRating] = useState(6);
  const [isClosing, setIsClosing] = useState(false);
  const { revalidateTicketDetails } = useTicketData();

  const closeTicket = async (comment) => {
    await ConnectriaTicketsService.closeTicket(id, comment, closeRating);
    //remove ticket from state and update UI
    stateTicketActions.removeTicket(id);
    // after making removal call, revalidate ticket details and refresh.
    revalidateTicketDetails();
    stateAppActions.setAlert({
      content: <p style={{ color: 'white' }}>Ticket #{id} closed.</p>,
      className: 'success',
      visible: true,
    });
    setTimeout(() => {
      stateAppActions.setAlert((prevState) => ({ ...prevState, visible: false }));
    }, 2000);
  };

  const handleClose = async (e) => {
    if (e) {
      e.preventDefault();
    }
    setIsClosing(true);
    try {
      if (reopen) {
        await ConnectriaTicketsService.reopenTicket(id, closeComment);
        //clear tickets to reset state
        stateTicketActions.setTicketStatus(id, 'Open');
        revalidateTicketDetails();
      } else {
        const comment = closeComment === '' ? 'Closed' : closeComment;
        await closeTicket(comment);
      }
    } catch (error) {
      console.log(error);
      stateAppActions.setBlade({
        visible: false,
        content: '',
        title: '',
      });
      stateAppActions.setAlert({
        content: <p style={{ color: 'white' }}>Error updating ticket.</p>,
        className: 'danger',
        visible: true,
      });
    }
  };

  return (
    <form className="mt-3" onSubmit={(e) => handleClose(e)}>
      <Label>
        <strong>Reason and Rating</strong>
      </Label>
      <p>
        How would you rate the support you received?{' '}
        {!reopen && (
          <span className="pl-2">
            <Button
              className={`btn-sm ${closeRating === 6 ? '' : 'btn-simple'}`}
              onClick={() => setCloseRating(6)}
              active={closeRating === 6}
              color="primary"
              style={{ border: '1px solid', borderColor: closeRating === 6 ? 'transparent' : '' }}
            >
              <i className="far fa-thumbs-up" />
            </Button>
            <Button
              className={`btn-sm ${closeRating === 3 ? '' : 'btn-simple'}`}
              onClick={() => setCloseRating(3)}
              active={closeRating === 3}
              color="primary"
              style={{ border: '1px solid', borderColor: closeRating === 3 ? 'transparent' : '' }}
            >
              <i className="far fa-thumbs-down" />
            </Button>
          </span>
        )}
      </p>
      <hr />
      <Input
        type="textarea"
        placeholder={
          reopen ? 'Re-Open Comment' : 'Please add a reason why this ticket is being closed (optional).'
        }
        onChange={(e) => setCloseComment(e.target.value)}
        rows="4"
        value={closeComment}
      />
      <div className="text-right mt-2">
        {isClosing ? (
          <Button type="submit" disabled={isClosing} className="btn-sm">
            <i className="fas fa-spinner fa-spin ml-1" style={{ fontSize: '15px' }} /> Submit
          </Button>
        ) : (
          <>
            {!reopen && (
              <Button type="submit" className="btn-sm">
                Submit
              </Button>
            )}
            {reopen && (
              <Button type="submit" disabled={closeComment.trim().split(/[\s\n]+/).length < 4} className="btn-sm">
                Submit
              </Button>
            )}
          </>
        )}
      </div>
    </form>
  );
};

const ConvertedDeviceId = (props) => {
  const [assetTag, setAssetTag] = useState(props.deviceId);
  const source = axios.CancelToken.source();

  useAsyncEffect(
    async (isMounted) => {
      try {
        var reg = new RegExp(/^\d+$/);

        if (reg.test(props.deviceId)) {
          let devices =
            (await ConnectriaTicketsService.getAllDevicesLike({
              ar_assoc: 'devicetype',
              limit: 100,
            })) || [];

          for (var i = 0; i < devices.length; i++) {
            if (devices[i].id === props.deviceId) setAssetTag(devices[i].assettag);
          }
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          // request cancelled
        } else {
          throw error;
        }
      }
    },
    () => {
      source.cancel();
    },
    []
  );

  return <span>{assetTag}</span>;
};

const CCForm = (props) => {
  return (
    <>
      <Label>
        <strong>Edit Ticket Recipients</strong>
      </Label>
      <div className="form-control" style={{ overflowY: 'scroll', height: 100 + 'px', minHeight: 100 + 'px' }}>
        {props.ccUsers.map((user, index) => (
          <div
            className={'d-flex w-100'}
            key={index}
            onClick={() => {
              props.handleCCSelect(user);
            }}
            style={{ backgroundColor: user.selected ? 'lightblue' : '', cursor: 'pointer' }}
          >
            <p style={{ flex: '0 50%' }}>{user.fname + ' ' + user.surname}</p>
            <p style={{ flex: '0 50%' }}>{'<' + user.email + '>'}</p>
          </div>
        ))}
      </div>
      {props.showCcCsm && (
        <div className="position-relative form-check ">
          <label className="form-check-label">
            <nobr>cc {props.csmInfo.user.fname + ' ' + props.csmInfo.user.surname}</nobr>
            <input
              type="checkbox"
              className="form-check-input"
              checked={props.includeCSM}
              onChange={props.handleCsmSelect}
            />
            <span className="form-check-sign">
              <span className="check" />
            </span>
          </label>
        </div>
      )}
      <Button
        className="btn-sm"
        style={{ minHeight: 18 + 'px' }}
        disabled={props.hasViewOnlyAccess}
        onClick={() => {
          props.handleCCSave();
        }}
      >
        Save
      </Button>
      <div className="mb-3" />
    </>
  );
};

const EMAIL_REGEX = /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(,[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})*$/;

const LECCForm = ({ requesterId: preRequesterId, recipients = [], ticketId, setShowCCForm, detail, hasViewOnlyAccess }) => {
  const [requesterList, setRequesterList] = useState([]);
  const [requesterId, setRequesterId] = useState(preRequesterId);
  const [additionalRecipients, setAdditionalRecipients] = useState(recipients.map(recipient => recipient.email).join(','));
  const [additionalRecipientsInvalid, setAdditionalRecipientsInvalid] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [CCSaveLoader, setCCSaveLoader] = useState(false);
  const { revalidateTicketDetails } = useTicketData();

  const handleCCSave = async () => {
    try {
      const allRecipients = [...additionalRecipients.split(','), inputValue.trim()]
        .filter(Boolean) // remove empty values
        .filter((email, index, self) => self.indexOf(email) === index); // deduplicate

      const invalidEmails = allRecipients.filter(email => !EMAIL_REGEX.test(email));
      if (invalidEmails.length > 0) {
        setAdditionalRecipientsInvalid(true);
        return;
      }

      setCCSaveLoader(true);

      // const orgId = CookieService.getCookie('OrganizationId');
      // await YetiTicketProvider.updateRecipients(orgId, ticketId, allRecipients, detail);
      await YetiTicketProvider.updateRecipients(ticketId, allRecipients, detail);
      if (requesterId !== preRequesterId) {
        // await YetiTicketProvider.modifyTicketRequester(orgId, ticketId, requesterId);
        await YetiTicketProvider.modifyTicketRequester(ticketId, requesterId);
        const requsterDetail = requesterList.filter(detail => detail.id === requesterId)[0];
        detail.user.fname = requsterDetail.first_name;
        detail.user.surname = requsterDetail.last_name;
      }
      revalidateTicketDetails();
      setCCSaveLoader(false);
      setShowCCForm(false);
    } catch (error) {
      console.log('error', error);
      setCCSaveLoader(false);
    }
  }

  const handleAdditionalRecipientChange = (value) => {
    if (EMAIL_REGEX.test(value) || !value) {
      setAdditionalRecipientsInvalid(false);
    } else {
      setAdditionalRecipientsInvalid(true);
    }

    setAdditionalRecipients(value);
  };

  const existingRecipients = useMemo(() => recipients.map(recipient => recipient.email));


  useEffect(() => {
    (async () => {
      setCCSaveLoader(true);

      let employeeList = await YetiContacts.contact_list();
      setRequesterList(employeeList.data.results.map(employee => ({ ...employee, description: `${employee.first_name} ${employee.last_name} (${employee.email})` })))

      setCCSaveLoader(false);
    })();
  }, []);

  if (CCSaveLoader) {
    return (
      <>
        {CCSaveLoader && (
          <div className="text-center">
            <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }}></i>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <Label>
        <strong>Edit Ticket Recipients</strong>
      </Label>

      <Label className="font-weight-bold">Ticket Requester</Label>
      <AutoCompleteSelectForm
        id="ddTicketRequester"
        onChange={(e) => setRequesterId(e.target.value)}
        options={requesterList}
        value={requesterId}
      />
      <div className='pt-2'>
        <Label className="font-weight-bold" for="txtDescription">
          Additional Recipients (CC's)
        </Label>
        <EmailSelector
          existingRecipients={existingRecipients}
          recipients={requesterList}
          onChange={handleAdditionalRecipientChange}
          onInputChange={setInputValue}
          setInvalidEmail={setAdditionalRecipientsInvalid}
        />
        {additionalRecipientsInvalid && (
          <span id="recipients-error" className="form-error">
            Invalid email address. Please ensure all email addresses are properly formatted (e.g., user@example.com)
          </span>
        )}
      </div>

      <Button
        className="btn-sm mt-3"
        style={{ minHeight: '18px' }}
        onClick={handleCCSave}
        disabled={additionalRecipientsInvalid || hasViewOnlyAccess}
        type='button'
      >
        Save
      </Button>
      <div className="mb-3"></div>
    </>
  );
}

const EditTicketDescription = (props) => {
  const [description, setDescription] = useState(props.detail?.issue);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { comments, provider } = useTicketData();

  if (!props.detail) return null;

  if (provider === PROVIDERS.LIGHTEDGE) {
    return (
      <div id="taDescription" className="mt-1 flex-grow-1 rounded mb-3">
        {comments.length > 0 ? comments[comments.length - 1].comment : ''}
      </div>
    );
  }

  if (['New', 'Open', 'WaitOnCust'].indexOf(props.detail?.status) === -1) {
    // show basic header without editing feature
    return (
      <div id="taDescription" className="mt-1 flex-grow-1 rounded mb-3">
        {props.detail?.issue}
      </div>
    );
  }

  const save = async (e) => {
    if (props.detail?.issue !== description) {
      setEditMode(false);
      setIsLoading(true);
      await ConnectriaTicketsService.updateTicket(props.detail?.id, { issue: description });
      setIsLoading(false);

      ConnectriaTicketsService.clearTicketCache();
      props.setReloadDetail(true);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  } else if (editMode) {
    return (
      <textarea
        value={description}
        className="form-control rounded"
        rows="10"
        onChange={(e) => {
          setDescription(e.target.value);
        }}
        onBlur={(e) => save(e)}
      />
    );
  } else {
    return (
      <div id="taDescription" onClick={() => setEditMode(true)} className="mt-1 flex-grow-1 rounded mb-3">
        {props?.detail?.issue}
      </div>
    );
  }
};

const BladeTabDetail = (props) => {
  const source = axios.CancelToken.source();
  const [closeFormVisible] = useState(false);
  const [reopenFormVisible, setReopenFormVisible] = useState(false);
  const [showCCForm, setShowCCForm] = useState(false);
  const [showCCSaveLoader, setCCSaveLoader] = useState(false);
  const [ccUsers, setCCUsers] = useState([]);
  const [includeCSM, setIncludeCSM] = useState(false);
  const [csmInfo, setCsmInfo] = useState({});
  const [showCcCsm, setShowCcCsm] = useState(false);
  const [notifyCsmMeta, setNotifyCsmMeta] = useState({});
  const [stateApp] = appState();
  const {provider, ticketDetailsErrors} = useTicketData();
  const hasViewOnlyAccess = stateApp.policies.includes("VIEW_ONLY_TICKETS");
  const momentTzGuess = moment.tz.guess();

  useAsyncEffect(
    async (isMounted) => {
      try {
        let isCustomer = stateApp.userInfo.organization_id !== 0;
        var users = await ConnectriaTicketsService.getAllUsersLike(
          {
            cancelToken: source.token,
          },
          isCustomer
        ) || [];
        let [accountAgents, metadata] = await Promise.all([
          ConnectriaTicketsService.getParamData({ cancelToken: source.token }),
          PortalService.getPortalCustomerMeta({ meta_key: 'tria_show_cc_csm' }),
        ]);
        users?.sort((a, b) => {
          return a.fname.localeCompare(b.fname);
        });
        let existsCCUsers = props.detail?.ticket_carbon_copies?.map((item) => {
          return item.user_id;
        }) || [];
        setCCUsers(
          users?.filter((user) => {
            user.selected = existsCCUsers.indexOf(user.id) > -1;
            return (
              user.active &&
              user.login_supported &&
              !user.serviceUser &&
              (props?.detail?.user?.email !== stateApp.userInfo?.email || user?.email !== stateApp.userInfo?.email)
            );
          })
        );

        accountAgents = accountAgents.dynamic.response.data.customer_account_agents || [];
        for (var agent of accountAgents) {
          if (agent.agent_type.name === 'Customer Success Manager') {
            setCsmInfo(agent);
          }
        }
        const notifyCsmTicketMeta = props?.detail?.meta?.find((meta) => meta?.meta_key === 'notify_csm');
        if (metadata?.meta_value === 'TRUE') {
          setShowCcCsm(true);
          if (notifyCsmTicketMeta) {
            setNotifyCsmMeta(notifyCsmTicketMeta);
            setIncludeCSM(true);
          }
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          // request cancelled
        } else {
          throw error;
        }
      }
    },
    () => {
      source.cancel();
    },
    []
  );

  if (ticketDetailsErrors) {
    return (
        <>
          <Alert color="danger">
            {ticketDetailsErrors.message}
          </Alert>
          <Button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-sm mt-1 d-block mx-auto"
          >
            Retry
          </Button>
        </>
    );
  }


  const handleCsmSelect = () => {
    setIncludeCSM(!includeCSM);
  };

  const handleCCSave = async () => {
    setShowCCForm(false);
    setCCSaveLoader(true);

    let selected_users = ccUsers.filter((user) => {
      return user.selected;
    });
    let selected_user_ids = selected_users.map((user) => {
      return user.id;
    });

    // delete unselected users
    let users = [];
    for (let item of props.detail.ticket_carbon_copies) {
      if (selected_user_ids.indexOf(item.user_id) > -1) {
        users.push(item);
      } else {
        await ConnectriaTicketsService.deleteTicketCarbonCopy(item.id);
      }
    }
    props.detail.ticket_carbon_copies = users;

    // add new users
    let exists_user_ids = props.detail?.ticket_carbon_copies?.map((user) => {
      return user.user_id;
    }) || [];
    for (let user of selected_users) {
      if (exists_user_ids.indexOf(user.id) === -1) {
        let cc = await ConnectriaTicketsService.saveTicketCarbonCopies(props.detail.id, [user.id]);
        props.detail.ticket_carbon_copies.push(cc[0]);
      }
    }
    // update user objects by user_id
    await ConnectriaTicketsService.updateUsersTicketCarbonCopies(props.detail);
    if (includeCSM && Object.keys(notifyCsmMeta).length === 0) {
      var response = await ConnectriaTicketsService.createTicketMeta(props.detail.id, 'notify_csm', 1);
      setNotifyCsmMeta(response.ticket_meta[0]);
      // Leave the checkbox checked
      setIncludeCSM(true);
    } else if (!includeCSM && Object.keys(notifyCsmMeta).length !== 0) {
      await ConnectriaTicketsService.deleteTicketMeta(notifyCsmMeta.id);
      setNotifyCsmMeta({});
      // Leave the checkbox unchecked
      setIncludeCSM(false);
    }
    setCCSaveLoader(false);
  };

  const handleCCSelect = (selectedUser) => {
    let usersCopy = ccUsers;

    for (let user of usersCopy) {
      if (user.id === selectedUser.id) {

        user.selected = !user.selected;
      }
    }

    setCCUsers(usersCopy.slice());
  };
  const getCustomerTicketId = ({ detail }) => {
    if (!detail) return null;

    const curiumIdMeta = detail.meta?.find((metaItem) => metaItem.meta_key === 'curium_sys_id');

    if (!curiumIdMeta) return null;

    return (
      <tr>
        <td>Customer Ticket #:</td>
        <td>
          <span className="font-weight-bold">{curiumIdMeta.meta_value}</span>
        </td>
      </tr>
    );
  };

  if (!props.detail) {
    return null
  }

  let statusDisplay = props.detail.status === 'WaitOnCust' ? 'Input Required' : props.detail.status;
  if (provider === PROVIDERS.LIGHTEDGE ) {
    // Display mapped status or fallback if not set
    const providerConfig = getProviderConfig(provider);
    const lowerCaseStatus = String(props.detail.status || '').toLowerCase();
    statusDisplay = providerConfig.ticketStatusMap[lowerCaseStatus]?.label || props.detail.status;
  }

  const isClosed = props.detail?.status?.toLowerCase() === 'closed';
  const hasCCs = (props.detail?.ticket_carbon_copies || []).length > 0;
  const showCCFormButton = !isClosed && (
    <Button
      type="button"
      onClick={() => setShowCCForm(!showCCForm)}
      className="btn-sm"
      disabled={hasViewOnlyAccess}
    >
      Edit Recipients
    </Button>
  );

  return (
      <div id="paneDetails" className="flex-fill d-flex flex-column tab-pane">
        <blockquote className={`mt-3 blockquote ticket-${props.detail?.status}`}>
          <table className="table table-sm table-borderless mb-0">
            <tbody>
            <tr>
              <td>{provider === PROVIDERS.CONNECTRIA ? 'Owner:' : 'Requester: '}</td>
              <td>
                <span className="font-weight-bold">{createdBy(props.detail?.user)}</span>
              </td>
            </tr>
            {provider === PROVIDERS.CONNECTRIA && (
                <tr>
                  <td>Created By:</td>
                  <td>
                    <span className="font-weight-bold">{createdBy(props.detail?.enteredby)}</span>
                  </td>
                </tr>
            )}
            <tr>
              <td>Created:</td>
              <td>
                <span className="font-weight-bold">
                  {props.detail?.created && moment(props.detail.created, 'YYYY-MM-DDThh:mm:ss.SSSZ').tz(momentTzGuess).format('LLL z')}
                </span>
              </td>
            </tr>

            {getCustomerTicketId({ detail: props.detail })}

            {provider === PROVIDERS.CONNECTRIA && (
              <tr>
                <td className="align-top">CC:</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>
                  <span className="font-weight-bold">
                    {(props.detail?.ticket_carbon_copies || [])
                      .map((item) => {
                        return ((item.user || {}).fname || '') + ' ' + ((item.user || {}).surname || '');
                      })
                      .join(', ')}
                  </span>
                </td>
              </tr>
            )}
            {provider === PROVIDERS.LIGHTEDGE && (
              <tr>
                <td className="align-top">CC:</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>
                  {props.detail?.recipients?.length > 0
                    ? props.detail?.recipients.map((item) => (
                      <div key={item.email}>
                        <span className="font-weight-bold">{item.email}</span>
                        <br />
                      </div>))
                    : <span className="font-weight-bold">No Recipients</span>
                  }
                </td>
              </tr>
            )}
            {provider === PROVIDERS.CONNECTRIA && <tr>
              <td>Type:</td>
              <td>
                <span className="font-weight-bold">
                  CASE
                </span>
              </td>
            </tr>}
            <tr>
              <td>Status:</td>
              <td>
                <span className="font-weight-bold">
                  {statusDisplay}
                </span>
              </td>
            </tr>
            {provider === PROVIDERS.CONNECTRIA && (
              <tr>
                <td>Category:</td>
                <td>
                  <span className="font-weight-bold">{props?.detail.ticketcategory?.name}</span>
                </td>
              </tr>
            )}
            {provider === PROVIDERS.CONNECTRIA && (
              <tr>
                <td>Sub-Category:</td>
                <td>
                  <span className="font-weight-bold">{props?.detail.ticketcategory?.subcategory}</span>
                </td>
              </tr>
            )}
            {Boolean(props?.detail?.device) ? (
                <tr>
                  <td>{provider === PROVIDERS.CONNECTRIA ? "Device:" : "Service:"}</td>
                  <td>
                    <span className="font-weight-bold">
                      {props.detail?.serviceListError ? (
                          <Alert color="danger" className="mb-2">
                            {props.detail.serviceListError}
                          </Alert>
                      ) : (
                          provider === PROVIDERS.LIGHTEDGE &&
                          props.detail?.service?.description && (
                              <span>{prependServiceIdToDescription(props.detail.service).description}</span>
                          )
                      )}

                      {provider === PROVIDERS.CONNECTRIA && (
                          <ConvertedDeviceId deviceId={props.detail.device}/>
                      )}
                    </span>
                  </td>
                </tr>
            ) : (
                <></>
            )}
            {(provider === PROVIDERS.CONNECTRIA || provider === PROVIDERS.LIGHTEDGE) && (
              <tr>
                <td />
                <td>
                  {provider === PROVIDERS.CONNECTRIA && hasCCs && <br />}
                  {provider === PROVIDERS.LIGHTEDGE && <br />}
                  {showCCFormButton}
                </td>
              </tr>
            )}
            </tbody>
          </table>
        </blockquote>
        {showCCSaveLoader && (
            <>
              <div className="text-center">
                <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{fontSize: '30px'}}/>
              </div>
            </>
        )}
        {showCCForm && provider === PROVIDERS.CONNECTRIA && (
            <>
              <CCForm
                  ccUsers={ccUsers}
                  handleCCSave={handleCCSave}
                  handleCCSelect={handleCCSelect}
                  showCcCsm={showCcCsm}
                  csmInfo={csmInfo}
                  includeCSM={includeCSM}
                  notifyCsmMeta={notifyCsmMeta}
                  handleCsmSelect={handleCsmSelect}
                  hasViewOnlyAccess={props?.hasViewOnlyAccess}
              />
            </>
        )}
        {showCCForm && provider === PROVIDERS.LIGHTEDGE && (
            <>
              {props.detail?.recipientsError ? (
                  <>
                    <Alert color="danger" className="mb-2">
                      {props.detail.recipientsError}
                    </Alert>
                    <Button
                        type="button"
                        onClick={() => window.location.reload()}
                        className="btn-sm mt-3 d-block mx-auto"
                    >
                      Retry
                    </Button>
                  </>
              ) : (
                  <LECCForm
                      requesterId={props.detail?.enteredBy}
                      recipients={props.detail?.recipients}
                      ticketId={props.detail?.id}
                      setShowCCForm={setShowCCForm}
                      detail={props?.detail}
                      hasViewOnlyAccess={props?.hasViewOnlyAccess}
                  />
              )}
            </>
        )}
        {provider === PROVIDERS.CONNECTRIA && <Label className="mt-1">
          <strong>Ticket Actions</strong>
        </Label>}
        {props.detail?.status?.toLowerCase() !== 'closed' ? (
            <div>
              {props.detail.status === 'Resolved' && (
                  <>
                    <Button
                        className="btn-sm"
                        id="toggleReopen"
                        disabled={closeFormVisible || props.hasViewOnlyAccess}
                        onClick={(e) => setReopenFormVisible(!reopenFormVisible)}
                    >
                      Re-Open Ticket
                    </Button>
                  </>
              )}

              {provider !== PROVIDERS.LIGHTEDGE && (
                  <>
                    <Button id="toggleClose" className="btn-sm" disabled={reopenFormVisible || props.hasViewOnlyAccess}>
                      Close Ticket
                    </Button>
                    <UncontrolledCollapse toggler="#toggleClose">
                      <TicketCloseForm
                          id={props.detail.id}
                          reopen={false}
                          fullPage={props.fullPage}
                      />
                    </UncontrolledCollapse>
                  </>
              )}
              {props.detail.status === 'Resolved' && (
                  <UncontrolledCollapse toggler="#toggleReopen">
                    <TicketCloseForm
                        id={props.detail.id}
                        reopen={true}
                        fullPage={props.fullPage}
                    />
                  </UncontrolledCollapse>
              )}
            </div>
        ) : (
            <div>
              <div className="alert alert-success w-auto">
                <i className="fas fa-check-circle"/> Ticket closed
              </div>
            </div>
        )}
        <>
          <Label className="mt-3">
            <strong>Description</strong>
          </Label>
          <EditTicketDescription detail={props.detail} setReloadDetail={props.setReloadDetail}/>
        </>

        {props.detail?.errorDesc?.length > 0 ? (
            <>
              <Label className="mt-3">
                <strong>Error Description</strong>
              </Label>
              <div id="taDescription" style={{whiteSpace: 'pre-wrap'}} className="mt-1 flex-grow-1 rounded mb-3">
                {props.detail.errorDesc}
              </div>
            </>
        ) : (
            <></>
        )}
        {(typeof props.showCommentsMsg === 'undefined' ? true : props.showCommentsMsg) && (
            <p>*You can view ticket history in the Comments tab.</p>
        )}
      </div>
  );
};

const initialState = {
  isPostingComment: false,
  attachments: [],
  attachmentError: null,
  commentError: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_POSTING':
      return { ...state, isPostingComment: action.payload };
    case 'ADD_ATTACHMENT':
      return { ...state, attachments: [...state.attachments, action.payload] };
    case 'SET_ATTACHMENT_ERROR':
      return { ...state, attachmentError: action.payload };
    case 'SET_COMMENT_ERROR':
      return { ...state, commentError: action.payload };
    case 'RESET_ATTACHMENTS':
      return { ...state, attachments: [], attachmentError: null };
    default:
      return state;
  }
};
const BladeTabComments = ({ detail, hasViewOnlyAccess }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [stateApp] = appState();
  const [cacheBuster, setCacheBuster] = useState(true);
  const [newComment, setNewComment] = useState('');
  const {
    isLoadingComments,
    provider,
    revalidateAttachments,
    revalidateComments,
    revalidateTicketDetails,
  } = useTicketData();
  const contactId = stateApp?.userInfo?.external_id || null;

  const changeCommentText = useCallback((text) => {
    setNewComment(text);
    dispatch({ type: 'SET_ATTACHMENT_ERROR', payload: null });
  }, []);

  const setAttachments = (files) => {
    dispatch({ type: 'RESET_ATTACHMENTS' });
    files.forEach((file) => {
      dispatch({ type: 'ADD_ATTACHMENT', payload: file });
    });
  };
  const setAttachmentError = (err) => {
    dispatch({ type: 'SET_ATTACHMENT_ERROR', payload: err });
  };

  const handleNewComment = useCallback(
    async (e) => {
      if (e) e.preventDefault();

      dispatch({ type: "SET_POSTING", payload: true });
      dispatch({ type: "SET_COMMENT_ERROR", payload: null });

      try {
        const hasComment = newComment.trim().length > 0;
        const hasAttachments = state.attachments.length > 0;

        // Ticket ID form validation.
        const ticketId = detail?.id;
        if (!ticketId) {
          throw new Error("Ticket ID is missing.");
        }

        // Provider form validation.
        if (!Object.values(PROVIDERS).includes(provider)) {
          throw new Error(
            "Unsupported ticket provider. Please try again or contact support at tickets@lightedge.com."
          );
        }

        // Contact ID form validation for LE users.
        if (provider === PROVIDERS.LIGHTEDGE) {
          if (!contactId) {
            throw new Error(
              "Submission failed. Your user is not associated with a valid Contact ID. Please try again or contact support at tickets@lightedge.com."
            );
          }
        }

        // Process attachment validation.
        let base64Attachments = [];
        if (hasAttachments) {
          base64Attachments = await Promise.all(
            state.attachments.map(async (attachment) => {
              try {
                const content = await FileService.toBase64(attachment);
                return { name: attachment.name, content };
              } catch {
                throw new Error(
                  `Failed to process attachment: ${attachment.name}. Please try again or contact support at tickets@lightedge.com.`
                );
              }
            })
          );
        }

        // Comment submission form validation.
        let commentPostedSuccessfully = false;

        if (hasComment) {
          try {
            switch (provider) {
              case PROVIDERS.CONNECTRIA:
                await ConnectriaTicketsService.newComment(
                  ticketId,
                  newComment.trim()
                );
                break;

              case PROVIDERS.LIGHTEDGE:
                await YetiTicketProvider.createTicketComment(
                  ticketId,
                  contactId,
                  newComment.trim()
                );
                break;
            }
            commentPostedSuccessfully = true;
          } catch {
            throw new Error(
              "Comment submission failed. Please try again or contact support at tickets@lightedge.com."
            );
          }
        }

        // Attachment form validation.
        if (hasAttachments) {
          const failedAttachmentUploads = [];
          let hasSuccessfulUpload = false;

          for (const attachment of base64Attachments) {
            try {
              switch (provider) {
                case PROVIDERS.CONNECTRIA:
                  await ConnectriaTicketsService.addAttachment(
                    ticketId,
                    attachment?.name,
                    attachment?.content
                  );
                  break;

                case PROVIDERS.LIGHTEDGE:
                  await YetiTicketProvider.createTicketAttachment(
                    ticketId,
                    contactId,
                    attachment?.name,
                    attachment?.name,
                    attachment?.content
                  );
                  break;
              }
              // Set attachment as successful if we get this far. Otherwise add it to the failedAttachmentUploads array.
              hasSuccessfulUpload = true;
            } catch {
              failedAttachmentUploads.push(attachment.name);
            }
          }

          // Partial success form validation and refresh.
          if (failedAttachmentUploads.length > 0) {
            // If one or more of the mass uploads or comments is successful reload the comments and show the attachments that CAN be uploaded.
            if (hasSuccessfulUpload || commentPostedSuccessfully) {
              // Partial success refresh what succeeded before showing error
              revalidateComments();
              revalidateAttachments();
              // Empty the comment field if the post was successful. 
              setNewComment("");
            }
            // We throw if there are any issues with the failed uploads and display which ones failed for clarity.
            throw new Error(
              `The following attachment(s) failed to upload: ${failedAttachmentUploads.join(
                ", "
              )}. ` +
                `Please verify the files are valid. If the issue persists, contact support at tickets@lightedge.com.`
            );
          }
        }

        // If we do NOT have any failed Attachments or comments, revalidate/refresh the ticket blade.
        revalidateComments();

        if (hasAttachments) {
          revalidateAttachments();
          revalidateTicketDetails();
        } else {
          setCacheBuster(true);
        }

        setNewComment("");
        dispatch({ type: "RESET_ATTACHMENTS" });
      } catch (err) {
        // Generic fallback message.
        const fallbackFormvalidationMessage = 
          "Something went wrong while submitting your request. Please try again or contact support at tickets@lightedge.com.";
        dispatch({
          type: "SET_COMMENT_ERROR",
          payload: err instanceof Error ? err.message : fallbackFormvalidationMessage,
        });
        dispatch({ type: "RESET_ATTACHMENTS" });
      } finally {
        dispatch({ type: "SET_POSTING", payload: false });
      }
    },
    [
      detail,
      provider,
      state.attachments,
      newComment,
      contactId,
      revalidateComments,
      revalidateAttachments,
      revalidateTicketDetails,
    ]
  );

  if (isLoadingComments) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  }

  return (
    <div id="paneComments" className="flex-fill d-flex flex-column tab-pane">
      {(detail?.status || "").toLowerCase() !== "closed" && (
        <form onSubmit={handleNewComment}>
          <Label className="mt-3">
            <strong>New Comment</strong>
          </Label>
          <Input
            onChange={(e) => changeCommentText(e.target.value)}
            type="textarea"
            id="taNewComment"
            rows="4"
            value={newComment}
            disabled={hasViewOnlyAccess}
          />
          <Attachments
            attachments={state.attachments}
            setAttachments={setAttachments}
            maxSizeMB={MAX_ATTACHMENT_SIZE_MB}
            error={`${state.commentError ?  state.commentError : ''} ${
              state.attachmentError ? state.attachmentError : ''
            }`}
            disabled={hasViewOnlyAccess}
            setError={setAttachmentError}
            label="Attach File(s)"
          />
          {!hasViewOnlyAccess && (
            <div className="text-right mt-2">
              <Button
                className="btn-sm"
                type="submit"
                disabled={
                  state.isPostingComment ||
                  (!state.attachments.length && newComment.trim().length < 3)
                }

              >
                {state.isPostingComment ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin ml-1"
                      style={{ fontSize: "15px" }}
                    />{" "}
                    Saving
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          )}
        </form>
      )}
      <TicketComments
        ticket={detail}
        ticketId={detail?.id}
        cacheBuster={cacheBuster}
        setCacheBuster={setCacheBuster}
      />
      <InitialCommentDescription ticket={detail} />
    </div>
  );
};

const BladeTabs = ({ fullPage, showCommentsMsg = true }) => {
  const [activeTab, setActiveTab] = useState(TABS_MAP.DETAILS);
  const {
    attachments,
    comments,
    isLoadingAttachments,
    isLoadingComments,
    ticketDetails,
    ticketDetailsErrors
  } = useTicketData();

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };
  const [stateApp] = appState();
  const hasViewOnlyAccess = stateApp.policies.includes("VIEW_ONLY_TICKETS");
  console.log('hasViewOnlyAccess', stateApp);

  if (ticketDetailsErrors) {
    return (
        <>
          <Alert color="danger">
            {ticketDetailsErrors.message}
          </Alert>
          <Button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-sm mt-1 d-block mx-auto"
          >
            Retry
          </Button>
        </>
    );
  }

  return (
    <>
      <ButtonGroup className="d-flex mb-2 justify-content-center" role="group">
        {TABS.map((label) => (
          <Button
            key={label}
            onClick={() => toggleTab(label)}
            active={activeTab === label}
            className={`btn-sm w-100 h-100 ${activeTab !== label ? 'btn-simple' : ''}`}
          >
            {label}
            {label === TABS_MAP.COMMENTS && (
              <Badge color="secondary" className="float-right">
                {isLoadingComments
                  ? <i className="fas fa-spinner fa-spin" style={{ fontSize: '6px' }} />
                  : comments?.length || 0
                }
              </Badge>
            )}
            {label === TABS_MAP.FILES && (
              <Badge color="secondary" className="float-right">
                {isLoadingAttachments
                  ? <i className="fas fa-spinner fa-spin" style={{ fontSize: '6px' }} />
                  : attachments?.length || 0
                }
              </Badge>
            )}
          </Button>
        ))}
      </ButtonGroup>

      {activeTab === TABS_MAP.DETAILS && (
        <BladeTabDetail
          detail={ticketDetails}
          showCommentsMsg={showCommentsMsg}
          fullPage={fullPage}
          hasViewOnlyAccess={hasViewOnlyAccess}
        />
      )}

      {activeTab === TABS_MAP.COMMENTS && (
        <BladeTabComments detail={ticketDetails} hasViewOnlyAccess={hasViewOnlyAccess} />
      )}

      {activeTab === TABS_MAP.FILES && (
        <div id="paneFiles" className="flex-fill d-flex flex-column tab-pane">
          <BladeTabAttachments ticket={ticketDetails} ticketId={ticketDetails?.id} />
        </div>
      )}
    </>
  )
};

function isTicket(obj) {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof obj.customer_id === 'number' &&
    typeof obj.enteredby === 'object' &&
    obj.enteredby !== null &&
    typeof obj.enteredby?.customer_id === 'number' &&
    typeof obj.created === 'string' &&
    typeof obj.issue === 'string'
  );
}

const InitialCommentDescription = ({ ticket }) => {
  const [stateApp] = appState();
  const momentTzGuess = moment.tz.guess();

  if (!isTicket(ticket)) return null;

  const customerId = ticket.enteredby?.customer_id || 0;

  return (
    <div className="mt-2 text-left">
      <div className="p-2 row" style={{ backgroundColor: '#e9e9e9', paddingBottom: 0 + 'px !important' }}>
        <div style={{ width: '90%' }} className="col">
          <p className="mb-0">
            Original Ticket created by:{' '}
            <span style={{ textTransform: 'capitalize', fontWeight: 700 }}>{createdBy(ticket.enteredby)}</span> of{' '}
            <span style={{ fontWeight: 700, textTransform: 'capitalize' }}>
              {/* TODO: check this with Sean */}
              {customerId === 0 ? 'Lightedge' : ticket.provider || stateApp.userInfo.organization_name}
            </span>
          </p>
          <p className=" mb-0">
            {moment(ticket.created, 'YYYY-MM-DDThh:mm:ss.SSSZ').tz(momentTzGuess).format('LLL z')}
          </p>
        </div>
        <div style={{ maxWidth: 10 + '%' }} className="col center-flex-item">
          {customerId === 0 ? (
            <img
              alt="Lightedge Logo"
              style={{ width: '30px' }}
              src="data:image/png;base64,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"
            />
          ) : (
            <></>
          )}
        </div>
      </div>
      <p className="pb-3 pt-1" style={{ fontSize: 0.97 + 'rem', whiteSpace: 'pre-wrap' }}>
        <Label className="mt-1">
          <strong>Description:</strong>
        </Label>
        <div id="taDescription" style={{ whiteSpace: 'pre-wrap' }} className="mt-1 rounded mb-3">
          {ticket.issue}
        </div>
        {ticket.errorDesc?.length > 0 ? (
          <>
            <Label className="mt-1">
              <strong>Error Description:</strong>
            </Label>
            <div id="taDescription" style={{ whiteSpace: 'pre-wrap' }} className="mt-1 rounded mb-3">
              {ticket.errorDesc}
            </div>
          </>
        ) : (
          <></>
        )}
      </p>
    </div>
  );
};

const EditTicketSubject = ({ detail }) => {
  const [subject, setSubject] = useState(detail?.subject);
  const [editMode, setEditMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { provider, revalidateTicketDetails } = useTicketData();
  const isNonEditableStatus = ['new', 'open', 'waitoncust'].indexOf(detail?.status?.toLowerCase()) === -1;

  if (provider === PROVIDERS.LIGHTEDGE || isNonEditableStatus) {
    // show basic header without editing feature
    return <h4>{detail?.subject}</h4>;
  }

  const style = {
    border: 'solid 1px #ddd',
    borderRadius: '3px',
    padding: '5px',
    fontSize: '18px',
    marginBottom: '15px',
  };

  const save = async () => {
    if (detail.subject !== subject) {
      setEditMode(false);
      setIsUpdating(true);
      await ConnectriaTicketsService.updateTicket(detail?.id, { subject: subject });
      setIsUpdating(false);

      ConnectriaTicketsService.clearTicketCache();
      revalidateTicketDetails();
    }
  };

  if (isUpdating) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  }

  if (editMode) {
    return (
      <input
        value={subject}
        style={style}
        className="form-control"
        maxLength={255}
        onChange={(e) => {
          setSubject(e.target.value);
        }}
        onBlur={(e) => save(e)}
      />
    );
  }

  return (
    <h4 style={style} onClick={() => setEditMode(true)}>
      {detail?.subject}
    </h4>
  );
};

const TABS_MAP = {
  COMMENTS: 'Comments',
  DETAILS: 'Details',
  FILES: 'Files',
};
const TABS = [TABS_MAP.DETAILS, TABS_MAP.COMMENTS, TABS_MAP.FILES];

const TicketBladeContainer = ({ id, ticketProvider }) => {
  const provider = ticketProvider || PROVIDERS.CONNECTRIA;

  if (!id) {
    return <Alert color="danger">Invalid ticket id.</Alert>;
  }

  return (
    <TicketDataProvider ticketId={id} provider={provider}>
      <ConnectriaTicketsDetailBlade />
    </TicketDataProvider>
  );
}

const ConnectriaTicketsDetailBlade = ({ fullPage = false }) => {
  const { ticketDetails, isLoadingTicketDetails, ticketDetailsErrors } = useTicketData();

  if (isLoadingTicketDetails) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  }

  if (ticketDetailsErrors?.message === "Unable to retrieve ticket") {
    return (
        <>
          <Alert color="danger">
            Unable to retrieve ticket data. Please try again.
          </Alert>
          <Button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-sm mt-1 d-block mx-auto"
          >
            Retry
          </Button>
        </>
    );
  }

  return (
    <div className="connectria-tickets-detail-blade h-100 d-flex flex-column">
      {isEmpty(ticketDetails) && !ticketDetailsErrors ? (
        <div className="text-center">
          <h4>Unable to retrieve ticket information</h4>
        </div>
      ) : (
        <>
          <EditTicketSubject detail={ticketDetails} />
          <BladeTabs fullPage={fullPage} />
        </>
      )}
    </div>
  );
};

const NewTabLink = (props) => {
  const { tooltipMessage, idx, className, style } = props;
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [text, setText] = useState(tooltipMessage);
  const toggle = () => {
    setTooltipOpen(!tooltipOpen);
    // When hovering off of tooltip, reset the message
    if (!tooltipOpen) setText(tooltipMessage);
  };
  const openNewTab = (e) => {
    e.preventDefault();
    window.open(props.link, '_blank');
    e.stopPropagation();
    return false;
  };
  return (
    <span onClick={(e) => openNewTab(e)}>
      <i
        className={'fas clickable fa-external-link-alt cursor pt-1 pl-1' + (className ? className : '')}
        id={'TooltipExample' + (idx + 1 ? idx : '')} // Index is optional
        style={{ fontSize: '0.9rem', ...style }}
      />
      <Tooltip
        placement="right"
        target={'TooltipExample' + (idx + 1 ? idx : '')} // Index is optional
        isOpen={tooltipOpen}
        toggle={toggle}
        style={{ backgroundColor: 'black', color: 'white' }}
      >
        {text}
      </Tooltip>
    </span>
  );
};
export { BladeTabAttachments, BladeTabComments, BladeTabDetail, BladeTabs, ConnectriaTicketsDetailBlade, EditTicketSubject, NewTabLink, TicketBladeContainer };

